import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { GenericSvg, SVGName } from "../../../assets/svgs/GenericSVG";
import { ButtonText } from "../../styles/stringStyles";
import { hexWithOpacity } from "../../utils/hexWithOpacity";
import { LoadingSpinner } from "../loading/LoadingSpinner";
import { CustomButton } from "./CustomButton";

export type ProgressButtonProps = {
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  numberOfSteps?: number;
  currentStep?: number;
  loading?: boolean;
  primaryColor?: string;
  alwaysActive?: boolean;
  iconName?: SVGName;
  className?: string;
  noMinWidth?: boolean;
};

export const ProgressButton = (props: ProgressButtonProps) => {
  const disabled =
    !props.alwaysActive &&
    (props.disabled ||
      (props.numberOfSteps !== undefined &&
        props.currentStep !== undefined &&
        props.currentStep < props.numberOfSteps));
  const [progress, setProgress] = useState(!props.numberOfSteps ? 100 : 0);
  const [duration, setDuration] = useState(0);
  const prevProgressRef = useRef(0);

  useEffect(() => {
    if (!props.numberOfSteps || props.currentStep === undefined) return;
    const newProgress = (props.currentStep / props.numberOfSteps) * 100;
    const progressDifference = Math.abs(newProgress - prevProgressRef.current);
    const newDuration = Math.max(0.2, Math.min(progressDifference * 0.01, 1));

    setDuration(newDuration);
    setProgress(newProgress);
    prevProgressRef.current = newProgress;
  }, [props.currentStep, props.numberOfSteps]);

  return (
    <Background className={props.className}>
      <StyledCustomButton
        onClick={props.onClick}
        disabled={disabled}
        loading={props.loading}
        color={props.primaryColor}
        $alwaysActive={props.alwaysActive}
        $hasIcon={props.iconName !== undefined}>
        <ProgressFill
          $progress={progress}
          $duration={duration}
          $color={props.primaryColor}
        />
        {props.iconName && <div />}
        {props.loading ? (
          <StyledLoadingSpinner />
        ) : (
          <StyledButtonText>{props.text}</StyledButtonText>
        )}
        {props.iconName && (
          <StyledGenericSvg
            name={props.iconName}
            size={24}
            primaryColor="white"
          />
        )}
      </StyledCustomButton>
    </Background>
  );
};

const Background = styled.div`
  border-radius: 100px;
  overflow: hidden;
`;

const StyledCustomButton = styled(CustomButton)<{
  $color?: string;
  $alwaysActive?: boolean;
  $hasIcon?: boolean;
}>`
  background-color: ${({ theme, $alwaysActive, $color }) =>
    $alwaysActive
      ? "#1c7792"
      : hexWithOpacity(
          $color ?? theme.colors.primary,
          0.5,
        )}; // TODO Check color @Fabian
  padding: 0 20px;
  border-radius: 100px;
  height: 50px;
  min-width: ${(props) => (props.$alwaysActive ? "200px" : "auto")};
  font-weight: bold;
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => (props.$hasIcon ? "space-between" : "center")};
  gap: 10px;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const ProgressFill = styled.div<{
  $progress: number;
  $duration: number;
  $color?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(props) => props.$progress}%;
  background-color: ${({ theme, $color }) => $color ?? theme.colors.primary};
  transition: width ${(props) => props.$duration}s ease-in-out;

  &::after {
    content: "";
    display: ${(props) => (props.$progress === 0 ? "none" : "block")};
    position: absolute;
    top: 0;
    right: -8px;
    bottom: 0;
    width: 16px;
    background-color: ${({ theme, $color }) => $color ?? theme.colors.primary};
    border-radius: 50% 50% 50% 50%;
  }
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
  z-index: 1;
`;

const StyledButtonText = styled(ButtonText)`
  color: ${({ theme }) => theme.colors.white};
  z-index: 1;
  justify-self: center;
`;

const StyledGenericSvg = styled(GenericSvg)`
  z-index: 1;
  justify-self: flex-end;
`;
